import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {NavItem} from 'app/components/NavSidebar';
import {useUserInfo} from 'app/utils/featureFlags';
import {getOrgPrefix} from 'app/utils/organizationUtils';

import cs from './styles.module.scss';

export const NavSidebarItem: React.FC<{
  item: NavItem;
  navItemPath: string;
  collapsedView: boolean;
  isProjectsLoading: boolean;
  expandedItems: string[];
  handleItemClick: (item: NavItem, navItemPath: string) => void;
}> = ({item, navItemPath, collapsedView, isProjectsLoading, expandedItems, handleItemClick}) => {
  const {title, icon, urlSlug, subItems, disabled, active} = item;

  const {pathname: currentUrlPath} = useLocation();
  const [currentOrganization] = useUserInfo();
  const navigate = useNavigate();

  const isActivePage = active(currentUrlPath, navItemPath, collapsedView);
  const isExpanded = expandedItems.includes(urlSlug);
  const isLoading = title === 'Portfolios' && isProjectsLoading;

  const menuItemContent = (
    <B.MenuItem
      multiline={!collapsedView}
      icon={typeof icon === 'function' ? icon() : <B.Icon icon={icon as B.IconName} />}
      text={collapsedView ? undefined : title}
      active={isActivePage}
      onClick={() => handleItemClick(item, navItemPath)}
      labelElement={
        subItems &&
        (collapsedView ? undefined : (
          <B.Button
            className={cs.expandButton}
            minimal
            small
            loading={isLoading}
            icon={isExpanded ? 'chevron-down' : 'chevron-right'}
          />
        ))
      }
      disabled={disabled}
    />
  );

  // If collapsed and has subitems, wrap in a Popover
  if (collapsedView && subItems) {
    const subItemsContent = (
      <B.Menu className={classnames(cs.navContent, cs.sidebar, cs.popover)}>
        {subItems.map((subItem) => {
          const subPath = `/${getOrgPrefix(currentOrganization!)}/${subItem.urlSlug}`;
          const isSubActive = subItem.active
            ? subItem.active(currentUrlPath, subPath, collapsedView)
            : false;

          return (
            <B.MenuItem
              multiline
              key={subPath}
              icon={typeof subItem.icon === 'function' ? subItem.icon() : subItem.icon}
              text={subItem.title}
              active={isSubActive}
              disabled={subItem.disabled}
              onClick={() => navigate(subPath)}
            />
          );
        })}
      </B.Menu>
    );

    return (
      <B.Popover
        key={urlSlug + title}
        content={subItemsContent}
        position="right-top"
        disabled={disabled}
        interactionKind="hover"
        minimal
      >
        {menuItemContent}
      </B.Popover>
    );
  }

  // If collapsed but no subitems, wrap in a Tooltip
  if (collapsedView && !subItems) {
    return (
      <B.Tooltip key={urlSlug + title} content={title} position="right" minimal disabled={disabled}>
        {menuItemContent}
      </B.Tooltip>
    );
  }

  // Default case: no wrapper needed
  return (
    <React.Fragment key={urlSlug + title}>
      {menuItemContent}
      {subItems && !collapsedView && (
        <B.Collapse isOpen={isExpanded}>
          <B.Menu className={cs.submenu}>
            {subItems.map((subItem) => {
              const subPath = `/${getOrgPrefix(currentOrganization!)}/${subItem.urlSlug}`;
              const isSubActive = subItem.active
                ? subItem.active(currentUrlPath, subPath, collapsedView)
                : false;

              return (
                <B.MenuItem
                  multiline
                  key={subPath}
                  icon={typeof subItem.icon === 'function' ? subItem.icon() : subItem.icon}
                  text={subItem.title}
                  active={isSubActive}
                  disabled={subItem.disabled}
                  onClick={() => navigate(subPath)}
                />
              );
            })}
          </B.Menu>
        </B.Collapse>
      )}
    </React.Fragment>
  );
};
